import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import StatusBadge, { UrgencyFlag } from "@/components/StatusBadge";
import AuthImage from "@/components/AuthImage";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Calendar, Clock, User, Phone, Mail, MapPin, Camera, FileText, Play, CheckCircle2, Tag, Droplets, Pencil, ClipboardList } from "lucide-react";
import { fmtDate, fmtTime } from "@/lib/format";
import { toast } from "sonner";

const CHECKLIST_ITEMS = [
  { key: "tested_leaks", label: "Tested for leaks" },
  { key: "photos_taken", label: "Photos taken" },
  { key: "signature_obtained", label: "Customer signature obtained" },
  { key: "invoice_sent", label: "Invoice sent" },
];

export default function JobDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoKind, setPhotoKind] = useState("before");
  const [showChecklist, setShowChecklist] = useState(false);
  const [checklist, setChecklist] = useState({});
  const [showEdit, setShowEdit] = useState(false);
  const [edit, setEdit] = useState({ title: "", scheduled_date: "", scheduled_time: "", tech_id: "" });
  const [team, setTeam] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/jobs/${id}`);
    setData(data);
    setNotes(data.job.notes || "");
    setChecklist(data.job.completion_checklist || {});
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const openEdit = async () => {
    if (team.length === 0) {
      const { data: t } = await api.get("/team");
      setTeam(t);
    }
    setEdit({
      title: data.job.title || "",
      scheduled_date: data.job.scheduled_date || "",
      scheduled_time: data.job.scheduled_time || "",
      tech_id: data.job.tech_id || "__unassigned__",
    });
    setShowEdit(true);
  };

  const saveEdit = async () => {
    if (!edit.title.trim()) { toast.error("Title required"); return; }
    setSavingEdit(true);
    try {
      await api.put(`/jobs/${id}`, {
        title: edit.title,
        scheduled_date: edit.scheduled_date,
        scheduled_time: edit.scheduled_time,
        tech_id: edit.tech_id === "__unassigned__" ? null : edit.tech_id,
      });
      toast.success("Job updated");
      setShowEdit(false);
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setSavingEdit(false); }
  };

  const tryMarkDone = () => {
    setShowChecklist(true);
  };

  const confirmDone = async () => {
    if (!Object.values(checklist).every(Boolean)) {
      toast.error("Complete all checklist items first");
      return;
    }
    try {
      await api.put(`/jobs/${id}`, { completion_checklist: checklist });
      await api.put(`/jobs/${id}/status`, { status: "done" });
      toast.success("Job complete — draft invoice created");
      setShowChecklist(false);
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const changeStatus = async (status) => {
    try {
      await api.put(`/jobs/${id}/status`, { status });
      toast.success("Status updated");
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/jobs/${id}`, { notes });
      toast.success("Notes saved");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setSavingNotes(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", photoKind);
      await api.post(`/jobs/${id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${photoKind === "before" ? "Before" : "After"} photo uploaded`);
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const triggerUpload = (kind) => {
    setPhotoKind(kind);
    setTimeout(() => fileRef.current?.click(), 0);
  };

  if (!data) {
    return (
      <div className="space-y-4">
        <div className="h-6 w-24 bg-slate-200 rounded animate-pulse" />
        <div className="h-40 bg-white rounded-2xl border border-slate-200 animate-pulse" />
        <div className="h-32 bg-white rounded-2xl border border-slate-200 animate-pulse" />
      </div>
    );
  }
  const { job, customer, tech, photos, invoice, estimate } = data;
  const beforePhotos = photos.filter((p) => p.kind !== "after");
  const afterPhotos = photos.filter((p) => p.kind === "after");

  return (
    <div className="space-y-5">
      <Link to="/jobs" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 font-semibold">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to jobs
      </Link>

      {estimate && (
        <Link to={`/estimates/${estimate.id}`} className="block" data-testid="linked-estimate">
          <div className="rounded-xl bg-blue-50 border border-blue-200 px-4 py-2.5 flex items-center gap-2 text-sm text-blue-900 hover:bg-blue-100 transition">
            <ClipboardList className="w-4 h-4" />
            <span>From estimate <b>{estimate.number}</b> — {estimate.title}</span>
          </div>
        </Link>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <UrgencyFlag urgency={job.urgency} />
              {job.job_type && (
                <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold bg-blue-50 text-blue-800 ring-1 ring-inset ring-blue-200">
                  <Tag className="w-3 h-3" />{job.job_type}
                </span>
              )}
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold" data-testid="job-title-text">{job.title}</h1>
            {job.issue_description && <p className="text-slate-700 mt-2 text-sm"><b>Issue:</b> {job.issue_description}</p>}
            {job.description && <p className="text-slate-600 mt-2 text-sm">{job.description}</p>}
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={job.status} />
            <Button onClick={openEdit} variant="outline" className="h-9 px-3 rounded-xl" data-testid="edit-job-btn">
              <Pencil className="w-4 h-4 mr-1" /> Edit
            </Button>
          </div>
        </div>

        <div className="mt-5 grid sm:grid-cols-2 gap-3 text-sm">
          <InfoRow icon={Calendar} label="Date" value={job.scheduled_date ? fmtDate(job.scheduled_date) : "—"} testId="job-date-val" />
          <InfoRow icon={Clock} label="Time" value={job.scheduled_time ? fmtTime(job.scheduled_time) : "—"} testId="job-time-val" />
          <InfoRow icon={User} label="Technician" value={tech ? tech.full_name : "Unassigned"} testId="job-tech-val" />
          <InfoRow icon={User} label="Customer" value={customer?.name || "—"} />
          {job.pipe_material && <InfoRow icon={Droplets} label="Pipe Material" value={job.pipe_material} />}
          {customer?.phone && <InfoRow icon={Phone} label="Phone" value={customer.phone} />}
          {customer?.email && <InfoRow icon={Mail} label="Email" value={customer.email} />}
          {customer?.address && <InfoRow icon={MapPin} label="Address" value={customer.address} />}
        </div>

        <div className="mt-5 flex gap-2 flex-wrap">
          {(job.status === "scheduled" || job.status === "unscheduled") && (
            <Button onClick={() => changeStatus("in_progress")} className="h-11 px-4 bg-orange-600 hover:bg-orange-700 font-semibold rounded-xl" data-testid="mark-in-progress">
              <Play className="w-4 h-4 mr-1.5" /> Mark In Progress
            </Button>
          )}
          {job.status === "in_progress" && (
            <Button onClick={tryMarkDone} className="h-11 px-4 bg-green-600 hover:bg-green-700 font-semibold rounded-xl" data-testid="mark-done">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark Complete
            </Button>
          )}
          {job.status === "done" && invoice && (
            <Link to={`/invoices/${invoice.id}`}>
              <Button className="h-11 px-4 bg-slate-900 hover:bg-slate-800 font-semibold rounded-xl" data-testid="view-invoice">
                <FileText className="w-4 h-4 mr-1.5" /> View Invoice
              </Button>
            </Link>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6">
        <h2 className="text-lg font-bold mb-3" style={{ fontFamily: "Outfit" }}>Notes</h2>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add notes about this job…" className="min-h-[110px]" data-testid="job-notes" />
        <Button onClick={saveNotes} disabled={savingNotes} className="mt-3 h-11 px-4 bg-blue-600 hover:bg-blue-700 font-semibold rounded-xl" data-testid="save-notes">
          {savingNotes ? "Saving…" : "Save Notes"}
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6">
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <h2 className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>Photos</h2>
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="photo-input" />
            <Button onClick={() => triggerUpload("before")} disabled={uploading} className="h-11 px-3 bg-slate-700 hover:bg-slate-800 font-semibold rounded-xl text-sm" data-testid="upload-before">
              <Camera className="w-4 h-4 mr-1" /> Before
            </Button>
            <Button onClick={() => triggerUpload("after")} disabled={uploading} className="h-11 px-3 bg-green-600 hover:bg-green-700 font-semibold rounded-xl text-sm" data-testid="upload-after">
              <Camera className="w-4 h-4 mr-1" /> After
            </Button>
          </div>
        </div>

        {photos.length === 0 ? (
          <p className="text-slate-500 text-sm">No photos yet. Add Before / After pictures.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PhotoColumn title="Before" photos={beforePhotos} tone="slate" />
            <PhotoColumn title="After" photos={afterPhotos} tone="green" />
          </div>
        )}
      </div>

      <Dialog open={showChecklist} onOpenChange={setShowChecklist}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Complete this job?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 mb-2">Check off each item before marking the job complete:</p>
          <div className="space-y-2">
            {CHECKLIST_ITEMS.map((c) => (
              <label key={c.key} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 cursor-pointer" data-testid={`checklist-${c.key}`}>
                <Checkbox
                  checked={!!checklist[c.key]}
                  onCheckedChange={(v) => setChecklist({ ...checklist, [c.key]: !!v })}
                />
                <span className="text-sm font-semibold">{c.label}</span>
              </label>
            ))}
          </div>
          <Button onClick={confirmDone} className="w-full h-12 bg-green-600 hover:bg-green-700 font-semibold rounded-xl mt-2" data-testid="confirm-done">
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark Complete
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Edit job</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-semibold">Title</Label>
              <Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className="h-12 mt-1.5" data-testid="edit-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-semibold">Date</Label>
                <Input type="date" value={edit.scheduled_date} onChange={(e) => setEdit({ ...edit, scheduled_date: e.target.value })} className="h-12 mt-1.5" data-testid="edit-date" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Time</Label>
                <Input type="time" value={edit.scheduled_time} onChange={(e) => setEdit({ ...edit, scheduled_time: e.target.value })} className="h-12 mt-1.5" data-testid="edit-time" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold">Technician</Label>
              <Select value={edit.tech_id} onValueChange={(v) => setEdit({ ...edit, tech_id: v })}>
                <SelectTrigger className="h-12 mt-1.5" data-testid="edit-tech"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__unassigned__">Unassigned</SelectItem>
                  {team.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.full_name} {m.role === "owner" ? "(Owner)" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={saveEdit} disabled={savingEdit} className="w-full h-12 bg-blue-600 hover:bg-blue-700 font-semibold rounded-xl" data-testid="edit-save">
              {savingEdit ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PhotoColumn({ title, photos, tone }) {
  const ring = tone === "green" ? "ring-green-200 bg-green-50" : "ring-slate-200 bg-slate-50";
  return (
    <div className={`rounded-xl ring-1 ring-inset ${ring} p-3`}>
      <div className="text-xs font-bold uppercase tracking-wide text-slate-700 mb-2">{title} ({photos.length})</div>
      {photos.length === 0 ? (
        <div className="text-xs text-slate-500">No {title.toLowerCase()} photos yet.</div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {photos.map((p) => (
            <div key={p.id} className="aspect-square overflow-hidden rounded-lg bg-white border border-slate-200" data-testid={`photo-${p.id}`}>
              <AuthImage photoId={p.id} alt={p.caption || title} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="h-8 w-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">{label}</div>
        <div className="text-sm text-slate-900 font-medium truncate" data-testid={testId}>{value}</div>
      </div>
    </div>
  );
}
